import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { SecondaryTextTypography } from '../../../App.styles';
import { useThemeContext } from '../../../global-state/themeContext';
import { AccessRequestItem } from '../../../hooks/useUserPermissions';
import { Icons } from '../../../stories/atoms';
import { ContainerV2 } from '../../../stories/atoms/containerV2/ContainerV2';
import { accessRequestTranslations } from '../../../translations/accessRequest';
import { getDaysLeftForAccessExpiry } from '../util';
import { StyledIconWithText } from './StatusTableitem.styles';

type StatusTableitemProps = {
  accessRequestItem: AccessRequestItem;
  position?: 'start' | 'end' | 'center';
  flexType?: 'row' | 'column';
};

export const StatusTableitem: FC<StatusTableitemProps> = ({
  accessRequestItem,
  position = 'start',
  flexType = 'column',
}) => {
  const { t } = useTranslation();
  const { statusTimestamp, status, requestTimestamp } = accessRequestItem;
  const { daysLeftText } = accessRequestTranslations;
  const { mode } = useThemeContext();

  const daysLeftForAccessExpiry = getDaysLeftForAccessExpiry(statusTimestamp, 365);
  const daysLeftForReview = getDaysLeftForAccessExpiry(requestTimestamp, 30);

  const isExpired = daysLeftForAccessExpiry <= 0;

  const statusMap = {
    Approved: {
      label: isExpired ? 'Expired' : 'Approved',
      icon: Icons.CHECK_MARK_CIRCLE,
      color: isExpired ? (mode === 'dark' ? 'error' : 'red') : mode === 'dark' ? 'success' : 'validationGreen',
      daysLeft: daysLeftForAccessExpiry,
      testId: 'approved-status',
    },
    Pending: {
      label: 'In Review',
      icon: Icons.HOURLASS,
      color: mode === 'dark' ? 'warningLight' : 'warningDark',
      daysLeft: daysLeftForReview,
      testId: 'pending-status',
    },
    Rejected: {
      label: 'Rejected',
      icon: Icons.CLOSEROUND,
      color: mode === 'dark' ? 'error' : 'red',
      daysLeft: null,
      testId: 'rejected-status',
    },
  } as const;

  const currentStatus = statusMap[status as keyof typeof statusMap];

  return (
    <ContainerV2
      display="flex"
      flexDirection={flexType === 'column' ? 'column' : 'row'}
      data-testid="status-table-item"
      alignItems={position}
      gap={flexType === 'column' ? [1] : [4]}
    >
      <StyledIconWithText
        data-testid={currentStatus.testId}
        label={currentStatus.label}
        textOptions={{
          color: currentStatus.color,
          type: 'caption',
        }}
        iconOptions={{
          icon: currentStatus.icon,
          size: 'small',
          color: currentStatus.color,
        }}
        containerStyles={{ justifyContent: 'start' }}
      />
      {currentStatus.daysLeft !== null && (
        <SecondaryTextTypography type="caption">
          {currentStatus.daysLeft} {t(daysLeftText.key, daysLeftText.defaultValue)}
        </SecondaryTextTypography>
      )}
    </ContainerV2>
  );
};
